import { type MouseEventHandler, type PropsWithChildren } from 'react'

import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { makeSelectUser } from '@webapp/core/stores/global'
import { useAppRoutes, useFeatureFlag } from '@webapp/hooks'
import { OrderDraftPreferredCreationFlows } from '@webapp/modules/purchasing/entities/enums/OrderDraftPreferredCreationFlowEnum'
import { useGetPreferredOrderRequestFlowPresenter } from '@webapp/modules/purchasing/features/get-preferred-order-request-flow/presenters/GetPreferredOrderRequestFlow'

export interface IOrderRequestLinkProps {
  title?: string
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

/**
 * Routes "Request order" CTAs to either the React or legacy Angular intake
 * page, depending on the user's eligibility for the Next-Gen intake flow.
 *
 * Gating both the LD flag AND the user's preferred flow at the call site
 * prevents the loader flash that the router-level redirect produces when a
 * non-eligible user lands on the React page only to be bounced back to
 * Angular. See useOrderRequestView.tsx for the redirect that this avoids.
 */
export const OrderRequestLink = ({
  title,
  onClick,
  children,
}: PropsWithChildren<IOrderRequestLinkProps>) => {
  const { url } = useAppRoutes()
  const { id: userId } = useSelector(makeSelectUser())
  const RFO_AUTOMATION = useFeatureFlag('RFO_AUTOMATION')
  const {
    data: { preferredOrderRequestFlow },
  } = useGetPreferredOrderRequestFlowPresenter(userId)

  const shouldUseReactRoute =
    RFO_AUTOMATION &&
    preferredOrderRequestFlow === OrderDraftPreferredCreationFlows.AI

  if (shouldUseReactRoute) {
    return (
      <Link to={url('OrderRequestPage')} title={title} onClick={onClick}>
        {children}
      </Link>
    )
  }

  return (
    <BridgeLink
      to={url('LegacyOrderRequestPage')}
      title={title}
      onClick={onClick}
    >
      {children}
    </BridgeLink>
  )
}

OrderRequestLink.displayName = 'OrderRequestLink'
